import React from 'react';
import { useDispatch } from 'react-redux';
import { Avatar, Button, Grid, Typography } from '@mui/material';

import useSettings from '../hooks/useSettings';
import Icon from '../Icon';
import { showImageEditorDialog } from '../image-editor/ImageEditorDialog';
import { showLogoGrabberDialog } from '../dialogs/LogoGrabberDialog';

interface UploadFileProps {
	/** The component css class name */
	className?: string;
	/** The component label helper text */
	label?: string;
	/** The field name */
	fieldName: string;
	/** The file name */
	fileName: string;
	/** The file base path */
	path: string;
	/** The field value */
	value?: string;
	/** Props used only with LogoGrabberDialog */
	grabberProps?: {
		/** The element default search string  */
		defaultSearch?: string;
	};
	/** Used to update the model with the modified field value */
	sendChange: (name: string, path: string, editedImage: File | null) => void;
	/** Used to show the image editor */
	showImageEditor?: boolean;
}

/** An input component used to upload a file */
function UploadFile({ showImageEditor = true, ...props }: UploadFileProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();

	const pickerRef = React.createRef<HTMLInputElement>();
	// const [file, setFile] = React.useState();

	const [uploadedImage, setUploadedImage] = React.useState<string | null>(null);

	function changeFile(event) {
		event.preventDefault();

		const image = event.target.files[0];
		const type = image.type.split('/')[1];
		const renamedImage = new File([image], `${props.fileName}.${type}`, { type: image.type });

		const reader = new FileReader();
		reader.addEventListener('load', () => setUploadedImage(reader.result as string));
		reader.readAsDataURL(image);

		props.sendChange(props.fieldName, `${sysSettings.files_path}\\${props.path}`, renamedImage);
		event.target.value = '';
		// setFile(renamedImage);
	}

	function deleteFile() {
		props.sendChange(props.fieldName, `${sysSettings.files_path}\\${props.path}`, null);
		setUploadedImage(null);
		// setFile(null);
	}

	function openImageEditor() {
		if (props.value) {
			dispatch(
				showImageEditorDialog({
					name: props.fieldName,
					path: props.path,
					value: props.value,
					uploadedImage: uploadedImage ?? undefined,
					setUploadedImage,
					sendChange: props.sendChange
				})
			);
		}
	}

	function openLogoGrabber() {
		dispatch(
			showLogoGrabberDialog({
				fieldName: props.fieldName,
				fileName: props.fileName,
				path: props.path,
				defaultSearch: props.grabberProps?.defaultSearch,
				sendFilesChange: props.sendChange,
				setUploadedImage
			})
		);
	}

	return (
		<div className={props.className}>
			<Grid container spacing={2}>
				<Grid>
					<Avatar
						className="h-64 w-64"
						variant="rounded"
						src={
							!uploadedImage && props.value
								? `${import.meta.env.VITE_ENDPOINT}/resources/${props.path}/${props.value}?v=${Date.now()}`
								: (uploadedImage ?? undefined)
						}
					>
						<Icon icon="image" size={32} />
					</Avatar>
				</Grid>
				<Grid size="grow">
					<Grid container spacing={1}>
						<Grid size={12}>
							{props.label && <Typography variant="subtitle2">{props.label}</Typography>}
							<Typography>{props.value || <i>{t('NO_FILE_UPLOADED')}</i>}</Typography>
						</Grid>
						<Grid>
							<Button variant="contained" size="small" onClick={() => pickerRef.current?.click()}>
								{t('FILE_EXPLORER_UPLOAD_FILE')}
							</Button>
							<input
								ref={pickerRef}
								id="outlined-button-file"
								type="file"
								accept="image/*"
								className="hidden"
								onChange={changeFile}
							/>
						</Grid>
						{props.grabberProps && (
							<Grid>
								<Button variant="contained" size="small" onClick={openLogoGrabber}>
									{t('DIALOG_LOGO_FIND')}
								</Button>
							</Grid>
						)}
						{props.value && (
							<>
								{showImageEditor && (
									<Grid>
										<Button variant="outlined" size="small" onClick={openImageEditor}>
											{t('VIEW_ITEM_BUTTON_IMAGE_EDITOR')}
										</Button>
									</Grid>
								)}
								<Grid>
									<Button variant="outlined" size="small" onClick={deleteFile} color="error">
										{t('VIEW_ITEM_BUTTON_DELETE')}
									</Button>
								</Grid>
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</div>
	);
}

export default React.memo(UploadFile);
